import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams } from 'react-router-dom';
import { fetchPlayersInfoAPI, IPlayersInfo } from '../../apis/tournament';
import { RaqtTable, Switcher } from '../../components';
import { IRaqtTableConfig } from '../../components/table/RaqtTable';
import { selectClassList } from '../../features/class/classSelectors';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { BackIcon } from '../../assets/icons/BackIcon';
import { fetchTournamentClassListPure } from '../../features/class/classActions';
import { CSVLink } from 'react-csv';
import * as ct from 'countries-and-timezones';

import './TournamentPlayersStats.scss';
import { Download } from 'react-bootstrap-icons';

type IPlayerList = {
  id: number;
  name: string;
  seed: number;
  users: string[][];
};

export const TournamentPlayersStats = () => {
  const prefix = 'tournamentStatsPlayers-';

  const { t } = useTranslation();
  const [playerList, setPlayerList] = useState<IPlayerList[]>([]);
  const [fullPlayerData, setFullPlayerData] = useState<IPlayersInfo[]>([]);
  const dispatch = useAppDispatch();
  const [classId, setClassId] = useState<number | null>(null);
  const { id, communityId } = useParams<{ id: string; communityId: string }>();
  const classes = useAppSelector(selectClassList);
  const [tournamentName, setTournamentName] = useState<string>('');

  const classList = [
    { label: 'All', value: null },
    ...classes.map(({ classId, name }) => ({
      value: classId,
      label: name,
    })),
  ];

  const getMatches = async () => {
    try {
      const { playersInfo, tournament } = await fetchPlayersInfoAPI(+id);

      const data = playersInfo.reduce((acc, value) => {
        const formatedData = value.teams.map(({ name, seed, users, type }) => ({
          id: value.id,
          name: value.name,
          users: [
            ...(type === 1 ? [[name]] : []),
            ...users.map((u) => [
              `${u.firstName ? u.firstName : ''} ${u.lastName ? u.lastName : ''}`,
              `${u.email ? u.email : ''}`,
              `${u.phone ? u.phone : ''}`,
              `DUPR: ${u.duprId || '-'} · Singles: ${u.duprRatingSingles ?? '-'} · Doubles: ${u.duprRatingDoubles ?? '-'}`,
            ]),
          ],
          seed,
        }));
        return [...acc, ...formatedData];
      }, [] as IPlayerList[]);
      setPlayerList(data);
      setFullPlayerData(playersInfo);
      setTournamentName(tournament.name);
    } catch (e) {
      setPlayerList([]);
      setFullPlayerData([]);
      setTournamentName('');
    }
  };

  const dataToShow = useMemo(() => {
    if (classId === null) return playerList;

    return playerList.filter(({ id }) => id === classId);
  }, [classId, playerList]);

  const tableConfig: IRaqtTableConfig = {
    blankHead: true,
    columns: [
      {
        label: t(prefix + 'class'),
        key: 'name',
        position: 'left',
      },
      {
        label: t(prefix + 'team'),
        key: 'users',
        position: 'left',
        columnSize: 'big',
      },
      {
        label: t(prefix + 'rank'),
        key: 'seed',
        position: 'left',
        columnSize: 'small',
      },
    ],
  };

  const downloadData = useMemo(() => {
    const filteredData =
      classId === null ? fullPlayerData : fullPlayerData.filter(({ id }) => id === classId);

    const allUsers = filteredData.flatMap(({ teams, id }) =>
      teams.flatMap(({ users }) =>
        users.map((user) => ({
          id: user.id,
          fullName: `${user.firstName || ''} ${user.lastName || ''}`,
          username: `${user.userName ? `@${user.userName}` : ''}`,
          email: user.email || '',
          phone: user.phone || '',
          dateOfBirth: user.dateOfBirth || '',
          duprId: user.duprId || '',
          duprRatingSingles: user.duprRatingSingles ?? '',
          duprRatingDoubles: user.duprRatingDoubles ?? '',
          licenseNumber: user.licenseNumber || '',
          country: user.countryCode ? ct.getCountry(user.countryCode)?.name || '' : ''
        }))
      )
    );

    const uniqueUsers = Array.from(new Map(allUsers.map((user) => [user.id, user])).values());

    return uniqueUsers;
  }, [fullPlayerData, classId]);

  useEffect(() => {
    getMatches();
    !classes.length && dispatch(fetchTournamentClassListPure(+id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, classId, classes]);

  const downloadFilename = useMemo(() => {
    return classId === null
      ? `Contacts - ${tournamentName}`
      : `Contacts - ${tournamentName} - ${classes.find((cls) => cls.classId === classId)?.name}`;
  }, [classId, classes, tournamentName]);

  const tableHolderRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const syncing = useRef(false);

  const updateScrollWidth = useCallback(() => {
    if (tableHolderRef.current) {
      setScrollWidth(tableHolderRef.current.scrollWidth);
    }
  }, []);

  useEffect(() => {
    updateScrollWidth();
    const observer = new ResizeObserver(updateScrollWidth);
    if (tableHolderRef.current) observer.observe(tableHolderRef.current);
    return () => observer.disconnect();
  }, [updateScrollWidth, dataToShow]);

  const handleTableScroll = () => {
    if (syncing.current) return;
    syncing.current = true;
    if (stickyScrollRef.current && tableHolderRef.current) {
      stickyScrollRef.current.scrollLeft = tableHolderRef.current.scrollLeft;
    }
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  };

  const handleStickyScroll = () => {
    if (syncing.current) return;
    syncing.current = true;
    if (tableHolderRef.current && stickyScrollRef.current) {
      tableHolderRef.current.scrollLeft = stickyScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  };

  return (
    <div className="tournament-players-stats">
      <div className="tournament-players-stats__title">{t(prefix + 'title')}</div>
      <div className="tournament-players-stats__back-link">
        <NavLink to={`/community/${communityId}/tournament/${id}/tags/dashboard`}>
          <BackIcon w="10" h="16" f="#0474EF" />{' '}
          <span className="tournament-players-stats__back-text">
            {t('tournamentDashboard-backButton')}
          </span>
        </NavLink>
      </div>
      <Switcher switches={classList} onSelect={setClassId} selectedValue={classId} />
      <CSVLink
        data={downloadData}
        headers={[
          { label: 'Name', key: 'fullName' },
          { label: 'Username', key: 'username' },
          { label: 'Email', key: 'email' },
          { label: 'Phone', key: 'phone' },
          { label: 'Date of Birth', key: 'dateOfBirth' },
          { label: 'Country', key: 'country' },
          { label: 'DUPR ID', key: 'duprId' },
          { label: 'DUPR Singles', key: 'duprRatingSingles' },
          { label: 'DUPR Doubles', key: 'duprRatingDoubles' },
          { label: 'License Number', key: 'licenseNumber' },
        ]}
        filename={`${downloadFilename}.csv`}
        className="btn btn-primary mt-4"
        target="_blank"
      >
        <Download className="mr-2 mb-1" size={16} />
        {classId === null
          ? t(prefix + 'downloadTournamentContacts')
          : t(prefix + 'downloadClassContacts')}
      </CSVLink>
      <div
        className="tournament-players-stats__table-holder"
        ref={tableHolderRef}
        onScroll={handleTableScroll}
      >
        <RaqtTable config={tableConfig} data={dataToShow} />
      </div>
      <div
        className="tournament-players-stats__sticky-scrollbar"
        ref={stickyScrollRef}
        onScroll={handleStickyScroll}
      >
        <div style={{ width: scrollWidth, height: 1 }} />
      </div>
    </div>
  );
};
